import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Precios from "@/pages/Precios";
import Aduanas from "@/pages/Aduanas";
import Inspectores from "@/pages/Inspectores";
import Presupuesto from "@/pages/Presupuesto";
import CostoProveedores from "@/pages/CostoProveedores";
import Compras from "@/pages/Compras";
import Calendario from "@/pages/Calendario";
import Usuarios from "@/pages/Usuarios";
import Empresas from "@/pages/Empresas";

const MODULE_ROUTES = [
  ["dashboard", "/"],
  ["costo_proveedores", "/costo-proveedores"],
  ["precios", "/precios"],
  ["aduanas", "/aduanas"],
  ["inspectores", "/inspectores"],
  ["presupuesto", "/presupuesto"],
  ["compras", "/compras"],
  ["calendario", "/calendario"],
];

function firstAllowedPath(can, isManager, isSuperadmin) {
  for (const [mod, path] of MODULE_ROUTES) {
    if (can(mod, "view")) return path;
  }
  if (isManager) return "/usuarios";
  if (isSuperadmin) return "/empresas";
  return null;
}

function ProtectedRoute({ children, managerOnly, superadminOnly }) {
  const { user, loading, isManager, isSuperadmin } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">Cargando…</div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (superadminOnly && !isSuperadmin) return <Navigate to="/" replace />;
  if (managerOnly && !isManager) return <Navigate to="/" replace />;
  return children;
}

function ModuleRoute({ module, children }) {
  const { can, isManager, isSuperadmin } = useAuth();
  if (can(module, "view")) return children;
  const landing = firstAllowedPath(can, isManager, isSuperadmin);
  if (landing && landing !== MODULE_ROUTES.find(([m]) => m === module)?.[1]) {
    return <Navigate to={landing} replace />;
  }
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center text-slate-400 gap-2" data-testid="sin-acceso-modulo">
      <p className="text-lg font-medium text-slate-600">Sin acceso a este módulo</p>
      <p className="text-sm">No tienes permiso para ver esta sección. Contacta a un administrador.</p>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<ModuleRoute module="dashboard"><Dashboard /></ModuleRoute>} />
        <Route path="/precios" element={<ModuleRoute module="precios"><Precios /></ModuleRoute>} />
        <Route path="/aduanas" element={<ModuleRoute module="aduanas"><Aduanas /></ModuleRoute>} />
        <Route path="/inspectores" element={<ModuleRoute module="inspectores"><Inspectores /></ModuleRoute>} />
        <Route path="/presupuesto" element={<ModuleRoute module="presupuesto"><Presupuesto /></ModuleRoute>} />
        <Route path="/costo-proveedores" element={<ModuleRoute module="costo_proveedores"><CostoProveedores /></ModuleRoute>} />
        <Route path="/compras" element={<ModuleRoute module="compras"><Compras /></ModuleRoute>} />
        <Route path="/calendario" element={<ModuleRoute module="calendario"><Calendario /></ModuleRoute>} />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute managerOnly>
              <Usuarios />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas"
          element={
            <ProtectedRoute superadminOnly>
              <Empresas />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
